import { useCallback, useEffect, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { subscribeTable } from '@/lib/realtime';
import type { HottrReportRow } from '@/lib/database.types';

export type ReportStatus = 'open' | 'reviewing' | 'resolved' | 'dismissed';

export interface ModReport extends HottrReportRow {
  /** Reported user's handle (joined from app_profiles when available). */
  reportedHandle?: string;
}

interface ReportsState {
  reports: ModReport[];
  loading: boolean;
  error: string | null;
  refresh(): Promise<void>;
  updateStatus(reportId: string, status: ReportStatus): Promise<{ ok: boolean; error?: string }>;
}

/**
 * Moderator-only feed of reports. Real-time via the `hottr_reports`
 * channel; status changes update the row in place.
 */
export const useReports = (filter: ReportStatus = 'open'): ReportsState => {
  const [reports, setReports] = useState<ModReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase
      .from('hottr_reports')
      .select('*')
      .eq('status', filter)
      .order('created_at', { ascending: false })
      .limit(200);
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    const rows = (data ?? []) as HottrReportRow[];

    // Best-effort handle join.
    const ids = Array.from(new Set(rows.map((r) => r.reported_id)));
    let handles: Record<string, string> = {};
    if (ids.length > 0) {
      const { data: profiles } = await supabase
        .from('app_profiles')
        .select('owner_id, handle')
        .in('owner_id', ids);
      const profileRows = (profiles ?? []) as Array<{ owner_id: string | null; handle: string | null }>;
      handles = Object.fromEntries(
        profileRows.map((p) => [p.owner_id ?? '', p.handle ?? '']),
      );
    }

    setReports(rows.map((r) => ({ ...r, reportedHandle: handles[r.reported_id] })));
    setError(null);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    load();
    const supabase = getSupabase();
    if (!supabase) return;
    const unsubscribe = subscribeTable(
      supabase,
      'admin-reports-watch',
      { event: '*', table: 'hottr_reports' },
      load,
    );
    return unsubscribe;
  }, [load]);

  const updateStatus = useCallback(
    async (reportId: string, status: ReportStatus): Promise<{ ok: boolean; error?: string }> => {
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'No connection.' };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;
      const { error: err } = (await db
        .from('hottr_reports')
        .update({ status, reviewed_at: new Date().toISOString() })
        .eq('id', reportId)) as { error: { message: string } | null };
      if (err) return { ok: false, error: err.message };
      return { ok: true };
    },
    [],
  );

  return { reports, loading, error, refresh: load, updateStatus };
};
